import { Box } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { GenericTable } from '../../../components/GenericTable'
import { useAccount } from '../../../context/account_context'
import { useLoading } from '../../../context/loading_context'
import { useSnack } from '../../../context/snack_context'
import { listVehicles } from '../../../services/vehicle'
import { useTranslation } from 'react-i18next'

export interface VehicleInfo {
    id?: number
    type: number
    modality: number
    brand: string
    idType: number
    document: string
    additionalInfo?: string | null
}

export interface VehicleFilters {
    page: number
    rows: number
    rowsCount: number
}

export enum VehicleType {
    Proper = 1,
    Third = 2,
}

export enum VehicleModality {
    Terrestrial = 1,
    River = 2,
    Aerial = 3,
    Multimodal = 4,
}

export enum VehicleIdentificationType {
    Id = 1,
    Registration = 2,
}

export const VehicleTypeLabel: Record<number, string> = {
    1: 'Próprio',
    2: 'Terceiro',
}

export const VehicleModalityLabel: Record<number, string> = {
    1: 'Terrestre',
    2: 'Fluvial',
    3: 'Aéreo',
    4: 'Multimodal',
}

export const VehicleIdentificationTypeLabel: Record<number, string> = {
    1: 'Número de documento',
    2: 'Número de matrícula',
}

export const VehicleHome = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const loading = useLoading()
    const snack = useSnack()
    const [vehicles, setVehicles] = useState<VehicleInfo[]>([])
    const [selectedVehicles, setSelectedVehicles] = useState<VehicleInfo[]>([])
    const [search, setSearch] = useState('')
    const timeoutRef = useRef<NodeJS.Timeout>()
    const { accountInfo } = useAccount()

    useEffect(() => {
        getVehicles()
    }, [])

    const getVehicles = async () => {
        loading.logicShow()
        try {
            const res = await listVehicles()

            setVehicles(res)
        } catch (error) {
            snack.connectionFail(error)
        }
        loading.logicHide()
    }

    const canCreateVehicle =
        accountInfo?.isMaster ||
        accountInfo?.rules.findIndex(
            (r) =>
                r.group.toLowerCase() === 'veículos' &&
                r.name.toLowerCase() === 'criar'
        ) !== -1

    const canUpdateVehicle =
        accountInfo?.isMaster ||
        accountInfo?.rules.findIndex(
            (r) =>
                r.group.toLowerCase() === 'veículos' &&
                r.name.toLowerCase() === 'atualizar'
        ) !== -1

    return (
        <>
            <Box>
                <GenericTable
                    title="190f0163-7ad0-41aa-bd38-ae61826d2346"
                    hidePagination
                    hideSearch
                    filters={[]}
                    search={''}
                    handleChangeSearch={() => {}}
                    handleChangeSelectedFilter={(selectedFilter) => {}}
                    handleChangeSelectedValues={(values) => {
                        setSelectedVehicles(
                            values.map((value) => {
                                const foundValue = selectedVehicles.find(
                                    (sv) => sv.id === value
                                )
                                if (foundValue) return foundValue
                                else
                                    return vehicles.filter(
                                        (v) => v.id === value
                                    )[0]
                            })
                        )
                    }}
                    selectedValues={selectedVehicles.map((v) => v.id as number)}
                    identifier="id"
                    values={vehicles}
                    columns={[
                        {
                            name: 'Tipo',
                            property: ['type'],
                            parser: (v) => t(VehicleTypeLabel[v]),
                        },
                        {
                            name: 'Modalidad',
                            property: ['modality'],
                            parser: (v) => t(VehicleModalityLabel[v]),
                        },
                        {
                            name: 'Tipo documento',
                            property: ['idType'],
                            parser: (v) => t(VehicleIdentificationTypeLabel[v]),
                        },
                        { name: 'Documento', property: ['document'] },
                        { name: 'Marca', property: ['brand'] },
                        {
                            name: 'Informaciones adicionales',
                            property: ['additionalInfo'],
                        },
                    ]}
                    actions={[
                        {
                            name: 'Acciones',
                            color: 'error',
                            dropdownActions: [
                                {
                                    name: 'Editar',
                                    color: 'secondary',
                                    hide: !canUpdateVehicle,
                                },
                            ],
                        },
                        {
                            name: 'Nuevo',
                            color: 'primary',
                            hide: !canCreateVehicle,
                        },
                    ]}
                    handleExecuteAction={(action) => {
                        if (action === 'Editar') {
                            if (selectedVehicles.length)
                                navigate(`edit/${selectedVehicles[0].id}`)
                        }
                        if (action === 'Nuevo') {
                            navigate('new')
                        }
                    }}
                />
            </Box>
        </>
    )
}
